import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, StatusBadge, ValuationWaterfall } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

export function SecuritiesPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['security', id],
    queryFn: () => api.security(id),
  });

  if (isLoading) return <PageSkeleton blocks={3} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Security dossier unavailable" />;

  const thesis = data.ownershipSummary ?? [];
  const valuation = (data as { valuation?: { models?: { spm_dcf?: { fairValuePerShare?: number }; dcf?: { fairValuePerShare?: number } } } }).valuation;
  const fairValue = valuation?.models?.spm_dcf?.fairValuePerShare ?? valuation?.models?.dcf?.fairValuePerShare ?? 112.8;
  const dcfBase = valuation?.models?.dcf?.fairValuePerShare ?? 108.4;

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.security?.name ?? 'Security'}
        subtitle={`${data.security?.symbol} · ${data.setupCandidates?.join(', ') ?? 'dossier'}`}
      />
      <ExplainActions screenName="Securities" />

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <AnalyticalPanel title="Ownership thesis (ACM default)" eyebrow={data.security?.symbol}>
          <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-[var(--color-text-secondary)]">
            {thesis.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.falsifiers?.map((f) => (
              <StatusBadge key={f} status="partial">
                {f.slice(0, 40)}…
              </StatusBadge>
            ))}
          </div>
        </AnalyticalPanel>

        <aside className="valuation-rail space-y-4">
          <AnalyticalPanel title="Valuation rail" subtitle="SPM-DCF">
            <ValuationWaterfall
              items={[
                { label: 'DCF base', value: dcfBase, kind: 'start' },
                { label: 'Protection adj.', value: 18, kind: 'positive' },
                { label: 'Terminal', value: 42, kind: 'positive' },
                { label: 'Fair value', value: fairValue, kind: 'total' },
              ]}
            />
          </AnalyticalPanel>
          <AnalyticalPanel title="Business model" tone="recessed">
            <p className="text-sm text-[var(--color-text-secondary)]">{data.businessModel}</p>
          </AnalyticalPanel>
        </aside>
      </div>
    </div>
  );
}
